import debounce from 'lodash/debounce'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import LightningIcon from 'assets/svg/app/lightning.svg'
import LinkIconLight from 'assets/svg/app/link-light.svg'
import Button from 'components/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxRow } from 'components/InfoBox'
import Spacer from 'components/Spacer'
import SplitSelect from 'components/SplitSelect'
import { Body } from 'components/Text'
import { ONE_CLICK_TRADING_DEFAULT_DEPOSIT_TOKEN } from 'constants/defaults'
import useClickOutside from 'hooks/useClickOutside'
import { useTimer } from 'hooks/useTimer'
import useAbstractionInfo from 'sections/futures/Trade/OneClickTrading/hooks/useAbstractionInfo'
import { setOpenModal } from 'state/app/reducer'
import { closeOneClickSession, startOneClickSession } from 'state/futures/actions'
import { setSelectedManageModalType, setTradingMode } from 'state/futures/reducer'
import { selectSubmittingFuturesTx } from 'state/futures/selectors'
import { ManageModalType, TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectIsSubAccountMode } from 'state/wallet/selectors'
import media from 'styles/media'
import { DateInterval, INTERVAL_OPTIONS } from 'utils/dates'

const SessionInfo: FC = () => {
	const { t } = useTranslation()
	const { sessionInfo, truncatedAddress, openWallet, balanceString, isSessionActive } =
		useAbstractionInfo()

	const timer = useTimer(sessionInfo?.validUntil ?? 0)

	return (
		<SessionInfoContainer>
			<SessionInfoTitle>{t('futures.one-click-trading.manage-session')}</SessionInfoTitle>
			<InfoBoxRow
				title={t('futures.one-click-trading.status.title')}
				nodeValue={
					<StatusNode>
						<Circle status={isSessionActive} />
						<span>
							{isSessionActive
								? t('futures.one-click-trading.status.active')
								: t('futures.one-click-trading.status.inactive')}
						</span>
					</StatusNode>
				}
			/>
			<InfoBoxRow title={t('futures.one-click-trading.expires-in')} textValue={timer} />
			<InfoBoxRow
				title={t('common.address')}
				textValue={truncatedAddress}
				textValueIcon={
					<LinkIconLight
						onClick={openWallet}
						height={16}
						width={16}
						style={{ paddingLeft: 5, cursor: 'pointer' }}
					/>
				}
			/>
			<InfoBoxRow
				title={t('futures.one-click-trading.eth-balance')}
				textValue={`${balanceString} ${ONE_CLICK_TRADING_DEFAULT_DEPOSIT_TOKEN}`}
			/>
		</SessionInfoContainer>
	)
}

const OneClickTradingActions: FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const { isSessionActive, isLowBalance, isDelegated } = useAbstractionInfo()

	const [open, setOpen] = useState(false)

	const [interval, setInterval] = useState<DateInterval>(INTERVAL_OPTIONS[0])

	const FORMATTED_INTERVAL_OPTIONS = INTERVAL_OPTIONS.map((s) => ({
		type: 'button' as const,
		value: s,
	}))

	const txProcessing = useAppSelector(selectSubmittingFuturesTx)
	const disabled = useMemo(() => txProcessing || isSubAccountMode, [isSubAccountMode, txProcessing])
	const openOnboard = useCallback(() => {
		setOpen(false)
		dispatch(setTradingMode(TradingModes.ONE_CLICK))
		dispatch(setOpenModal('one_click_trading_onboard'))
	}, [dispatch])

	const toggleSession = useCallback(async () => {
		if (isLowBalance) {
			dispatch(setSelectedManageModalType(ManageModalType.OneClick))
			return dispatch(setOpenModal('futures_deposit_withdraw_smart_margin'))
		} else if (isSessionActive) {
			return dispatch(closeOneClickSession())
		} else {
			return dispatch(startOneClickSession(interval))
		}
	}, [dispatch, isLowBalance, isSessionActive, interval])

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const toggleOpen = useCallback(
		debounce(() => {
			setOpen((prevOpen) => !prevOpen)
		}, 100),
		[]
	)

	const { ref } = useClickOutside(toggleOpen)

	return (
		<>
			<IconButton active={isSessionActive} size="small" onClick={toggleOpen}>
				<LightningIcon width={10} height={14} />
			</IconButton>
			{open && (
				<TradingTabContainer ref={ref}>
					{isDelegated ? (
						<>
							<SessionInfo />
							{!isSessionActive && (
								<>
									<Spacer height={20} />
									<SplitSelect<DateInterval>
										selected={interval}
										options={FORMATTED_INTERVAL_OPTIONS}
										onSelect={(_interval) => setInterval(_interval!)}
									/>
								</>
							)}
							{isLowBalance && (
								<ErrorView
									messageType="warn"
									message={t('futures.one-click-trading.low-balance-warning')}
									containerStyle={{
										margin: '20px 0',
										padding: '20px',
									}}
								/>
							)}
							<StyledButton
								loading={txProcessing}
								noOutline
								disabled={disabled}
								onClick={toggleSession}
							>
								{isLowBalance
									? t('futures.one-click-trading.onboard.deposit')
									: isSessionActive
									? t('futures.one-click-trading.onboard.stop-session')
									: t('futures.one-click-trading.onboard.start-session')}
							</StyledButton>
						</>
					) : (
						<StyledButton
							style={{ marginTop: '0' }}
							noOutline
							disabled={isSubAccountMode}
							onClick={openOnboard}
						>
							{t('futures.one-click-trading.onboard.activate')}
						</StyledButton>
					)}
					{isSubAccountMode && (
						<ErrorView
							messageType="error"
							message={t('futures.market.trade.confirmation.modal.disabled-delegation-trading')}
							containerStyle={{ margin: '16px 0 0 0' }}
						/>
					)}
				</TradingTabContainer>
			)}
		</>
	)
}

export default OneClickTradingActions

const TradingTabContainer = styled.div`
	z-index: 100;
	position: absolute;
	right: 3%;

	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
	padding: 20px;
	border-radius: 15px;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	min-width: 420px;

	${media.lessThan('mdUp')`
		padding: 15px;
	`}

	${media.greaterThan('mdUp')`
		margin-top: 56px;
	`}
`

export const Circle = styled.div<{ status: boolean }>`
	z-index: 2;
	height: 9px;
	width: 9px;
	border-radius: 50%;
	background-color: ${({ status, theme }) => {
		switch (status) {
			case true:
				return theme.colors.common.palette.green.g500
			case false:
				return theme.colors.common.palette.red.r300
		}
	}};
`

export const StatusNode = styled(Body)`
	display: flex;
	align-items: center;
	gap: 5px;
`

const SessionInfoTitle = styled(Body)`
	margin-bottom: 20px;
	font-size: 13px;
	line-height: 1.2;
	font-family: ${(props) => props.theme.fonts.bold};
`

const IconButton = styled(Button)<{ active: boolean }>`
	color: ${(props) =>
		props.active
			? props.theme.colors.selectedTheme.newTheme.containers.secondary.background
			: props.theme.colors.common.palette.yellow.y500};
	background-color: ${(props) =>
		props.active
			? props.theme.colors.common.palette.yellow.y500
			: props.theme.colors.selectedTheme.newTheme.button.default.background};

	&:hover {
		color: ${(props) => props.theme.colors.common.palette.yellow.y500};
	}
`

const StyledButton = styled(Button)`
	margin-top: 24px;
	height: 50px;
	width: 100%;
`

const SessionInfoContainer = styled.section`
	min-width: 390px;
`
